import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../app/api';

const initialState = {
    projectAnalysis: null,
    companies: null,
    companyDetails: null,
    invoiceData: null,
    expensesData: null,
    receiptData: null,
    monthReportData: null,
    loading: {
        projectAnalysis: false,
        companies: false,
        companyDetails: false,
        invoiceData: false,
        expensesData: false,
        receiptData: false,
        monthReportData: false,
        settings: false,
        insuranceTypes: false,
    },
    error: null,
    settings: {
        min_insurance_income: 0,
        max_insurance_income: 0,
    },
    insuranceTypes: {
        types: [],
        loading: false,
        error: null,
    }
};

export const getProjectAnalysis = createAsyncThunk(
    'admin/getProjectAnalysis',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/admin/project-analysis');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getCompanies = createAsyncThunk(
    'admin/getCompanies',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/admin/companies');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getCompanyDetails = createAsyncThunk(
    'admin/getCompanyDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/companies/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getInvoiceDataDetails = createAsyncThunk(
    'admin/getInvoiceDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/invoices/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateInvoiceDataDetails = createAsyncThunk(
    'admin/updateInvoiceDataDetails',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/invoices/update/${id}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const deleteInvoiceDataDetails = createAsyncThunk(
    'admin/deleteInvoiceDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/admin/invoices/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getExpenseDataDetails = createAsyncThunk(
    'admin/getExpenseDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/expenses/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateExpenseDataDetails = createAsyncThunk(
    'admin/updateExpenseDataDetails',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/expenses/update/${id}`, { data });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const deleteExpenseDataDetails = createAsyncThunk(
    'admin/deleteExpenseDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/admin/expenses/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getReceiptDataDetails = createAsyncThunk(
    'admin/getReceiptDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/receipts/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateReceiptDataDetails = createAsyncThunk(
    'admin/updateReceiptDataDetails',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/receipts/update/${id}`, { data });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const deleteReceiptDataDetails = createAsyncThunk(
    'admin/deleteReceiptDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/admin/receipts/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getMonthReportDataDetails = createAsyncThunk(
    'admin/getMonthReportDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/month-report/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateMonthReportDataDetails = createAsyncThunk(
    'admin/updateMonthReportDataDetails',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/month-report/update/${id}`, { data });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const deleteMonthReportDataDetails = createAsyncThunk(
    'admin/deleteMonthReportDataDetails',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/admin/month-report/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getSettings = createAsyncThunk(
    'admin/getSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/admin/settings');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateMinMaxSettings = createAsyncThunk(
    'admin/updateMinMaxSettings',
    async ({ min_insurance_income, max_insurance_income }, { rejectWithValue }) => {
        try {
            const response = await api.post('/admin/settings/update', { min_insurance_income, max_insurance_income });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getInsuranceTypes = createAsyncThunk(
    'admin/getInsuranceTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/admin/settings/insurance-types');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateInsuranceTypeSettings = createAsyncThunk(
    'admin/updateInsuranceTypeSettings',
    async ({ insurance_types }, { rejectWithValue }) => {
        try {
            const response = await api.post('/admin/settings/insurance-types', { insurance_types });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProjectAnalysis.pending, (state) => {
                state.loading.projectAnalysis = true;
                state.error = null;
            })
            .addCase(getProjectAnalysis.fulfilled, (state, action) => {
                state.loading.projectAnalysis = false;
                state.projectAnalysis = action.payload;
                state.error = null;
            })
            .addCase(getProjectAnalysis.rejected, (state, action) => {
                state.loading.projectAnalysis = false;
                state.error = action.payload;
                state.projectAnalysis = null;
            })
            .addCase(getCompanies.pending, (state) => {
                state.loading.companies = true;
                state.error = null;
            })
            .addCase(getCompanies.fulfilled, (state, action) => {
                state.loading.companies = false;
                state.companies = action.payload;
                state.error = null;
            })
            .addCase(getCompanies.rejected, (state, action) => {
                state.loading.companies = false;
                state.companies = null;
                state.error = action.payload;
            })
            .addCase(getCompanyDetails.pending, (state) => {
                state.loading.companyDetails = true;
                state.error = null;
            })
            .addCase(getCompanyDetails.fulfilled, (state, action) => {
                state.loading.companyDetails = false;
                state.companyDetails = action.payload;
                state.error = null;
            })
            .addCase(getCompanyDetails.rejected, (state, action) => {
                state.loading.companyDetails = false;
                state.error = action.payload;
            })
            .addCase(getInvoiceDataDetails.pending, (state) => {
                state.loading.invoiceData = true;
                state.error = null;
            })
            .addCase(getInvoiceDataDetails.fulfilled, (state, action) => {
                state.loading.invoiceData = false;
                state.invoiceData = action.payload;
                state.error = null;
            })
            .addCase(getInvoiceDataDetails.rejected, (state, action) => {
                state.loading.invoiceData = false;
                state.error = action.payload;
            })
            .addCase(updateInvoiceDataDetails.pending, (state) => {
                state.loading.invoiceData = true;
                state.error = null;
            })
            .addCase(updateInvoiceDataDetails.fulfilled, (state, action) => {
                state.loading.invoiceData = false;
                // Update the invoice data in state
                if (state.invoiceData && state.invoiceData.id === action.meta.arg.id) {
                    state.invoiceData = { ...state.invoiceData, ...action.meta.arg.data };
                }
                state.error = null;
            })
            .addCase(updateInvoiceDataDetails.rejected, (state, action) => {
                state.loading.invoiceData = false;
                state.error = action.payload;
            })
            .addCase(deleteInvoiceDataDetails.pending, (state) => {
                state.loading.invoiceData = true;
                state.error = null;
            })
            .addCase(deleteInvoiceDataDetails.fulfilled, (state, action) => {
                state.loading.invoiceData = false;
                state.invoiceData = action.payload;
                state.error = null;
            })
            .addCase(deleteInvoiceDataDetails.rejected, (state, action) => {
                state.loading.invoiceData = false;
                state.error = action.payload;
            })
            .addCase(getExpenseDataDetails.pending, (state) => {
                state.loading.expensesData = true;
                state.error = null;
            })
            .addCase(getExpenseDataDetails.fulfilled, (state, action) => {
                state.loading.expensesData = false;
                state.expensesData = action.payload;
                state.error = null;
            })
            .addCase(getExpenseDataDetails.rejected, (state, action) => {
                state.loading.expensesData = false;
                state.error = action.payload;
            })
            .addCase(updateExpenseDataDetails.pending, (state) => {
                state.loading.expensesData = true;
                state.error = null;
            })
            .addCase(updateExpenseDataDetails.fulfilled, (state, action) => {
                state.loading.expensesData = false;
                // Update the expense data in state
                if (state.expensesData && state.expensesData.id === action.meta.arg.id) {
                    state.expensesData = { ...state.expensesData, ...action.meta.arg.data };
                }
                state.error = null;
            })
            .addCase(updateExpenseDataDetails.rejected, (state, action) => {
                state.loading.expensesData = false;
                state.error = action.payload;
            })
            .addCase(deleteExpenseDataDetails.pending, (state) => {
                state.loading.expensesData = true;
                state.error = null;
            })
            .addCase(deleteExpenseDataDetails.fulfilled, (state, action) => {
                state.loading.expensesData = false;
                state.expensesData = action.payload;
                state.error = null;
            })
            .addCase(deleteExpenseDataDetails.rejected, (state, action) => {
                state.loading.expensesData = false;
                state.error = action.payload;
            })
            .addCase(getReceiptDataDetails.pending, (state) => {
                state.loading.receiptData = true;
                state.error = null;
            })
            .addCase(getReceiptDataDetails.fulfilled, (state, action) => {
                state.loading.receiptData = false;
                state.receiptData = action.payload;
                state.error = null;
            })
            .addCase(getReceiptDataDetails.rejected, (state, action) => {
                state.loading.receiptData = false;
                state.error = action.payload;
            })
            .addCase(updateReceiptDataDetails.pending, (state) => {
                state.loading.receiptData = true;
                state.error = null;
            })
            .addCase(updateReceiptDataDetails.fulfilled, (state, action) => {
                state.loading.receiptData = false;
                // Update the receipt data in state
                if (state.receiptData && state.receiptData.id === action.meta.arg.id) {
                    state.receiptData = { ...state.receiptData, ...action.meta.arg.data };
                }
                state.error = null;
            })
            .addCase(updateReceiptDataDetails.rejected, (state, action) => {
                state.loading.receiptData = false;
                state.error = action.payload;
            })
            .addCase(deleteReceiptDataDetails.pending, (state) => {
                state.loading.receiptData = true;
                state.error = null;
            })
            .addCase(deleteReceiptDataDetails.fulfilled, (state, action) => {
                state.loading.receiptData = false;
                state.receiptData = action.payload;
                state.error = null;
            })
            .addCase(deleteReceiptDataDetails.rejected, (state, action) => {
                state.loading.receiptData = false;
                state.error = action.payload;
            })
            .addCase(getMonthReportDataDetails.pending, (state) => {
                state.loading.monthReportData = true;
                state.error = null;
            })
            .addCase(getMonthReportDataDetails.fulfilled, (state, action) => {
                state.loading.monthReportData = false;
                state.monthReportData = action.payload;
                state.error = null;
            })
            .addCase(getMonthReportDataDetails.rejected, (state, action) => {
                state.loading.monthReportData = false;
                state.error = action.payload;
            })
            .addCase(updateMonthReportDataDetails.pending, (state) => {
                state.loading.monthReportData = true;
                state.error = null;
            })
            .addCase(updateMonthReportDataDetails.fulfilled, (state, action) => {
                state.loading.monthReportData = false;
                // Update the month report data in state
                if (state.monthReportData && state.monthReportData.id === action.meta.arg.id) {
                    state.monthReportData = { ...state.monthReportData, ...action.meta.arg.data };
                }
                state.error = null;
            })
            .addCase(updateMonthReportDataDetails.rejected, (state, action) => {
                state.loading.monthReportData = false;
                state.error = action.payload;
            })
            .addCase(deleteMonthReportDataDetails.pending, (state) => {
                state.loading.monthReportData = true;
                state.error = null;
            })
            .addCase(deleteMonthReportDataDetails.fulfilled, (state, action) => {
                state.loading.monthReportData = false;
                state.monthReportData = action.payload;
                state.error = null;
            })
            .addCase(deleteMonthReportDataDetails.rejected, (state, action) => {
                state.loading.monthReportData = false;
                state.error = action.payload;
                state.monthReportData = null;
            })
            .addCase(getSettings.pending, (state) => {
                state.loading.settings = true;
                state.error = null;
            })
            .addCase(getSettings.fulfilled, (state, action) => {
                state.loading.settings = false;
                state.settings = action.payload;
                state.error = null;
            })
            .addCase(getSettings.rejected, (state, action) => {
                state.loading.settings = false;
                state.error = action.payload;
            })
            .addCase(updateMinMaxSettings.pending, (state) => {
                state.loading.settings = true;
                state.error = null;
            })
            .addCase(updateMinMaxSettings.fulfilled, (state, action) => {
                state.loading.settings = false;
                state.settings = action.payload;
                state.error = null;
            })
            .addCase(updateMinMaxSettings.rejected, (state, action) => {
                state.loading.settings = false;
                state.error = action.payload;
            })
            .addCase(getInsuranceTypes.pending, (state) => {
                state.loading.insuranceTypes = true;
                state.error = null;
            })
            .addCase(getInsuranceTypes.fulfilled, (state, action) => {
                state.loading.insuranceTypes = false;
                // Replace the entire insuranceTypes object
                state.insuranceTypes = {
                    types: Array.isArray(action.payload) ? action.payload : [],
                    loading: false,
                    error: null
                };
                state.error = null;
            })
            .addCase(getInsuranceTypes.rejected, (state, action) => {
                state.loading.insuranceTypes = false;
                state.error = action.payload;
            })
            .addCase(updateInsuranceTypeSettings.pending, (state) => {
                state.loading.insuranceTypes = true;
                state.error = null;
            })
            .addCase(updateInsuranceTypeSettings.fulfilled, (state, action) => {
                state.loading.insuranceTypes = false;
                // Replace the entire insuranceTypes object
                state.insuranceTypes = {
                    types: Array.isArray(action.payload) ? action.payload : [],
                    loading: false,
                    error: null
                };
                state.error = null;
            })
            .addCase(updateInsuranceTypeSettings.rejected, (state, action) => {
                state.loading.insuranceTypes = false;
                state.error = action.payload;
            })
    }
});

export default adminSlice.reducer;